<template>
    <Foreground
        ref="refForeground"
        :visible="fgVisible"
        :active="enable.foreground"
        :initialAlpha="fgProps.initialAlpha"
        :layer="fgProps.layer"
        :blending="fgProps.blending"
    />
    <BorderGlow ref="refBorderGlow" :active="enable.borderGlow" :visible="bgVisible" />
    <PatternPlanes
        ref="refPatternPlanes"
        :active="enable.patternPlanes"
        :visible="patternVisible"
    />

    <slot />
</template>

<script>
    import { NormalBlending } from 'three';
    import { inject } from 'vue';

    import { LAYER_BG } from '~/core/constants';

    export const BgEffectsKey = Symbol('BgEffects');

    export const useBgEffects = () => {
        const provider = inject(BgEffectsKey);

        if (!provider) {
            throw new Error('BgEffects provider not found');
        }

        return provider;
    };

    export const InstantWaxFgProps = { initialAlpha: 0, layer: LAYER_BG, blending: NormalBlending };
    export const InstantWaxEnabledEffects = {
        foreground: true,
        borderGlow: true,
        patternPlanes: true,
    };

    export const PATTERN_MIN_ALPHA = 0.05;
    export const PATTERN_MAX_ALPHA = 0.3;
    export const BORDER_GLOW_MIN_BRIGHTNESS = 0.2;
    export const BORDER_GLOW_MAX_BRIGHTNESS = 1;
    export const BORDER_GLOW_MIN_ROTATE_SPEED = 0.15;
    export const BORDER_GLOW_MAX_ROTATE_SPEED = 0.3;
    export const BORDER_GLOW_MIN_BLUR_LEVEL = 0.25;
    export const BORDER_GLOW_MAX_BLUR_LEVEL = 0.4;
    export const FG_MIN_ALPHA = 0.5;
    export const FG_MAX_ALPHA = 0.7;
    export const FG_MIN_RADIUS = 1.6;
    export const FG_MAX_RADIUS = 1.3;
</script>

<script setup>
    import { AdditiveBlending } from 'three';
    import { computed, nextTick, onBeforeUnmount, provide, ref, watch } from 'vue';

    import { lerp } from '@resn/gozer-math';
    import { usePane } from '@resn/gozer-vue';

    import BorderGlow from '~/components/gl/Cards/reveals/wax-chrome/modules/border-glow/BorderGlow.vue';
    import PatternPlanes from '~/components/gl/Cards/reveals/wax-chrome/modules/pattern-bg/PatternPlanes.vue';
    import Foreground from '~/components/gl/Foreground/index.vue';
    import { LAYER_FG } from '~/core/constants';

    const EFFECT_FADE_DURATION = 0.8;

    const props = defineProps({
        enable: {
            type: Object,
            default: () => ({ foreground: true, borderGlow: false, patternPlanes: false }),
        },
        fgProps: {
            type: Object,
            default: () => ({ initialAlpha: 1, layer: LAYER_FG, blending: AdditiveBlending }),
        },
    });

    const enable = computed(() => props.enable);
    const fgProps = computed(() => props.fgProps);

    const refBorderGlow = ref(null);
    const refPatternPlanes = ref(null);
    const refForeground = ref(null);

    const fgVisible = ref(false);
    const bgVisible = ref(false);
    const patternVisible = ref(false);
    const intensity = ref(0);

    const setForeground = (active, { animate = true } = {}) => {
        const duration = animate ? EFFECT_FADE_DURATION : 0;

        if (active) {
            fgVisible.value = true;
            refForeground.value?.show({ alpha: props.fgProps.initialAlpha, duration });
        } else {
            refForeground.value?.hide({
                duration,
                onComplete: () => {
                    fgVisible.value = false;
                },
            });
        }
    };

    const setBorderGlow = (active, { animate = true } = {}) => {
        const duration = animate ? EFFECT_FADE_DURATION : 0;

        if (active) {
            bgVisible.value = true;
            refBorderGlow.value?.fadeIn({ duration });
        } else {
            refBorderGlow.value?.hide({
                duration,
                onComplete: () => {
                    bgVisible.value = false;
                },
            });
        }
    };

    const setPatternPlanes = (active, { animate = true } = {}) => {
        const duration = animate ? EFFECT_FADE_DURATION : 0;

        if (active) {
            patternVisible.value = true;
            refPatternPlanes.value?.show();
        } else {
            refPatternPlanes.value?.hide({
                duration,
                alpha: 0,
                onComplete: () => {
                    patternVisible.value = false;
                },
            });
        }
    };

    const applyEnable = async (nextEnable, prevEnable, { animate = true } = {}) => {
        await nextTick();

        const prev = prevEnable ?? {
            foreground: false,
            borderGlow: false,
            patternPlanes: false,
        };

        if (nextEnable.foreground !== prev.foreground) {
            setForeground(nextEnable.foreground, { animate });
        } else if (nextEnable.foreground && !fgVisible.value) {
            setForeground(true, { animate: false });
        }

        if (nextEnable.borderGlow !== prev.borderGlow) {
            setBorderGlow(nextEnable.borderGlow, { animate });
        } else if (nextEnable.borderGlow && !bgVisible.value) {
            setBorderGlow(true, { animate: false });
        }

        if (nextEnable.patternPlanes !== prev.patternPlanes) {
            setPatternPlanes(nextEnable.patternPlanes, { animate });
        } else if (nextEnable.patternPlanes && !patternVisible.value) {
            setPatternPlanes(true, { animate: false });
        }
    };

    watch(
        () => props.enable,
        (nextEnable, prevEnable) => {
            applyEnable(nextEnable, prevEnable, { animate: prevEnable !== undefined });
        },
        { deep: true, immediate: true }
    );

    watch(
        () => props.fgProps.initialAlpha,
        (alpha, prevAlpha) => {
            if (prevAlpha === undefined || !props.enable.foreground) return;
            refForeground.value?.show({ alpha, duration: EFFECT_FADE_DURATION });
        }
    );

    const showForeground = (opts) => {
        refForeground.value.show(opts);
    };

    const hideForeground = (opts) => {
        refForeground.value.hide(opts);
    };

    const updateForeground = (opts) => {
        refForeground.value.updateProps(opts);
    };

    const revealChromeEffects = ({ intensity: int = 0, pulse: addPulse = false } = {}) => {
        intensity.value = int;

        const revealIntensity = int || intensity.value;

        if (revealIntensity === 0) return;

        const delay = 0;
        const pulseDelay = 0.5;
        const duration = 1.5;
        const outStart = duration;

        refForeground.value.reveal({
            delay,
            inAlpha: lerp(revealIntensity, FG_MIN_ALPHA, FG_MAX_ALPHA),
            inMaskRadius: lerp(revealIntensity, FG_MIN_RADIUS, FG_MAX_RADIUS),
            inDuration: duration,
            inEase: 'sine.out',
            outAlpha: 0,
            outMaskRadius: FG_MIN_RADIUS,
            outDuration: 1,
            outEase: 'sine.out',
            outStart,
        });

        const brightness = lerp(
            revealIntensity,
            BORDER_GLOW_MIN_BRIGHTNESS,
            BORDER_GLOW_MAX_BRIGHTNESS
        );
        const rotateSpeed = lerp(
            revealIntensity,
            BORDER_GLOW_MIN_ROTATE_SPEED,
            BORDER_GLOW_MAX_ROTATE_SPEED
        );
        const blurLevel = lerp(
            revealIntensity,
            BORDER_GLOW_MIN_BLUR_LEVEL,
            BORDER_GLOW_MAX_BLUR_LEVEL
        );
        const patternAlpha = lerp(revealIntensity, PATTERN_MIN_ALPHA, PATTERN_MAX_ALPHA);

        refBorderGlow.value?.show({
            delay,
            brightness,
            rotateSpeed,
            blurLevel,
            addPulse,
            pulseDelay,
            outStart,
        });
        refPatternPlanes.value?.show({
            delay,
            alpha: patternAlpha,
            addPulse,
            pulseDelay,
            outStart,
        });
    };

    const showBorderGlow = (opts) => {
        refBorderGlow.value.show(opts);
    };

    const hideBorderGlow = (opts) => {
        refBorderGlow.value.hide(opts);
    };

    const showPatternPlanes = (opts) => {
        refPatternPlanes.value.show(opts);
    };

    const hidePatternPlanes = (opts) => {
        refPatternPlanes.value.hide(opts);
    };

    const hideAll = () => {
        hideBorderGlow();
        hidePatternPlanes();
    };

    provide(BgEffectsKey, {
        showForeground,
        hideForeground,
        updateForeground,
        revealChromeEffects,
        showBorderGlow,
        showPatternPlanes,
        hideBorderGlow,
        hidePatternPlanes,
        hideAll,
    });

    usePane([], {
        title: 'Background Effects',
        expanded: false,
    });
    usePane([], {
        title: 'Main',
        expanded: true,
    });

    defineExpose({
        showForeground,
        hideForeground,
        updateForeground,
        showBorderGlow,
        showPatternPlanes,
        hideBorderGlow,
        hidePatternPlanes,
        hideAll,
    });
</script>

<style lang="scss" scoped></style>
